import React from 'react';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import SmartToyIcon from '@mui/icons-material/SmartToy';

/* ==========================================================================
   Shared types for AssistantChat config-driven rendering
   ========================================================================== */

export type ExampleGroup = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  prompts: string[];
};

export type Breadcrumb = { label: string; href?: string };

export type SyncSend = {
  mode: 'sync';
  url: string;
  bodyExtras?: Record<string, any>;
};

export type AsyncSend = {
  mode: 'async';
  postUrl: string;
  bodyExtras: Record<string, any>;
  pollIntervals?: number[];
  maxPollDuration?: number;
  eventsPollInterval?: number;
};

export type WaitingActivity = {
  steps: Array<{ atMs: number; label: string }>;
  longWaitAfterMs: number;
  longWaitMessage: string;
  liveEvents?: {
    recentEventLimit: number;
    activeLabels: Record<string, string>;
    defaultActiveLabel: string;
    longWaitMessage: string;
  };
};

export type AssistantChatConfig = {
  /** Identity of the assistant shown in headers/avatars */
  assistant: { name: string; avatar: string; accent?: string };
  /** Page-level breadcrumb above the card */
  breadcrumb?: Breadcrumb[];
  /** Sidebar header title (top-of-sidebar bold text) */
  sidebarTitle: string;
  /** Sidebar header subtitle (e.g. "AI Chat", "Q3 2026 Earnings Call") */
  sidebarSubtitle?: string;
  /** Optional context block under the sidebar title (price/industry/links) */
  contextHeader?: React.ReactNode;
  /** Short single-sentence intro shown in the empty hero */
  introContext: React.ReactNode;
  /** Suggestion examples (single-tab or multi-tab) */
  examples: ExampleGroup[];
  /** Initial selected example tab index */
  initialExampleTab?: number;
  /** Backend endpoints */
  api: {
    previousMessagesUrl: string;
    send: SyncSend | AsyncSend;
  };
  /** Optional client-side progress shown while waiting for a non-streaming response. */
  waitingActivity?: WaitingActivity;
  /** Optional toggles */
  features?: {
    rename?: boolean;
    newChat?: boolean;
    activityEvents?: boolean;
    discussionUrlPush?: boolean;
    throttledState?: boolean;
  };
  /** Builder for download filename */
  downloadFilenameBuilder: (snippet: string) => string;
  /** Initial server-rendered state */
  initial?: {
    threads?: any[];
    discussionId?: any;
    discussionTitle?: string;
    currentUser?: string;
    /** From server (`signed_in`); when false, show sign-in gate immediately (universal /chat). */
    signedIn?: boolean;
  };
  /** Title to show after `pushState` for Universal */
  discussionPageTitle?: string;
};

const DEFAULT_POLL_INTERVALS = [1000, 3000, 7000, 15000, 30000, 60000];
const DEFAULT_MAX_POLL_DURATION = 180000;
const DEFAULT_EVENTS_POLL_INTERVAL = 2000;

const ASSISTANT_ACCENT: Record<string, string> = {
  Lima: '#34c759',
  Saturday: '#1e88e5',
  June: '#0ea5e9',
  Summer: '#f59e0b',
};

function accentFor(name: string): string {
  return ASSISTANT_ACCENT[name] || '#6c5ce7';
}

/* ==========================================================================
   Universal Chat (Lima — top-level /chat)
   ========================================================================== */

export function universalChatConfig({
  agent,
  data,
  showAgent = false,
}: {
  agent?: string;
  data?: any;
  showAgent?: boolean;
}): AssistantChatConfig {
  const name = agent || 'Lima';
  const discussionId = data?.discussion_id;
  const previousMessagesUrl =
    discussionId != null && discussionId !== ''
      ? `/chat/${discussionId}/previous_messages` + (showAgent ? '?show_agent=true' : '')
      : showAgent
        ? '?show_agent=true'
        : '';

  const examples: ExampleGroup[] = [
    {
      id: 'analysis',
      label: 'Analysis',
      icon: <AnalyticsIcon fontSize="small" />,
      prompts: [
        "Summarize Apple's latest earnings",
        "Analyze Microsoft's income statements for the last 10 years",
        "What are NVIDIA's expectations for the coming quarter and year?",
        "Summarize Tesla's last 3 earnings calls",
      ],
    },
    {
      id: 'compare',
      label: 'Compare',
      icon: <CompareArrowsIcon fontSize="small" />,
      prompts: [
        "Compare Coca-Cola (KO) with Pepsico's cash flows for the last ten years, which one is more profitable?",
        "Compare Costco to its top 3 competitors",
      ],
    },
    {
      id: 'screener',
      label: 'Stock Screener',
      icon: <SearchIcon fontSize="small" />,
      prompts: [
        'Find S&P 500 companies that pay high dividends',
        'Find S&P 500 and S&P 400 stocks with value scores > 80 and payback time < 10 years',
        'Show me US-based stocks with a value score above 80',
      ],
    },
    {
      id: 'sec',
      label: 'SEC Filings',
      icon: <ArticleIcon fontSize="small" />,
      prompts: [
        "What is Apple Inc.'s primary sources of revenue, and how have they changed over the past year from their latest 10-K?",
        "Summarize the key financial and product highlights from Microsoft's latest 10-Q filing?",
        "What is the outlook for Costco based on the management discussion from their latest 10-K?",
        "Summarize NVIDIA's 2023 10-k",
      ],
    },
    {
      id: 'agents',
      label: 'AI Agents',
      icon: <SmartToyIcon fontSize="small" />,
      prompts: [
        'Create a project to determine if I should buy NVIDIA stock',
        'Create a project to perform a DCF valuation on Apple stock',
        'Monitor Apple stock and notify me of any important events and red flags',
      ],
    },
  ];

  return {
    assistant: { name, avatar: `/images/${name.toLowerCase()}_avatar.jpg`, accent: accentFor(name) },
    breadcrumb: [
      { label: 'Home', href: '/' },
      { label: 'AI Chat' },
    ],
    sidebarTitle: data?.title || 'Untitled Chat',
    sidebarSubtitle: 'AI Chat',
    introContext: 'AI Stock Market Research Analyst — ask me anything about stocks, earnings, filings, or screening.',
    examples,
    initialExampleTab: showAgent ? 4 : 0,
    api: {
      previousMessagesUrl,
      send: {
        mode: 'async',
        postUrl: '/async_chat_messages',
        bodyExtras: { message_type: 'universal_chat' },
        pollIntervals: DEFAULT_POLL_INTERVALS,
        maxPollDuration: DEFAULT_MAX_POLL_DURATION,
        eventsPollInterval: DEFAULT_EVENTS_POLL_INTERVAL,
      },
    },
    waitingActivity: {
      steps: [
        { atMs: 0, label: 'Understanding your question' },
        { atMs: 3000, label: 'Planning the research approach' },
        { atMs: 8000, label: 'Preparing the first research step' },
      ],
      longWaitAfterMs: 15000,
      longWaitMessage: 'Still working — detailed research can take a little longer.',
      liveEvents: {
        recentEventLimit: 3,
        activeLabels: {
          thinking: 'Continuing the analysis',
          reasoning: 'Continuing the analysis',
          tool_call: 'Working on this research step',
        },
        defaultActiveLabel: 'Continuing the analysis',
        longWaitMessage: 'Still working — this research step can take a little longer.',
      },
    },
    features: {
      rename: true,
      newChat: true,
      activityEvents: true,
      discussionUrlPush: true,
    },
    downloadFilenameBuilder: (snippet: string) =>
      `${snippet.slice(0, 20)}_analysis_of_${data?.ticker ?? 'chat'}_generated_by_${name}_ai_assistant.txt`,
    initial: {
      threads: data?.conversation_threads || [],
      discussionId: data?.discussion_id,
      discussionTitle: data?.title,
      currentUser: data?.current_user,
      signedIn: !!data?.signed_in,
    },
    discussionPageTitle: 'AI Stock Market Research Analyst | Instant Market Insights | Decode Investing',
  };
}

/* ==========================================================================
   Earnings Call Chat (Saturday)
   ========================================================================== */

export function earningsCallChatConfig({
  ticker: _ticker,
  transcript,
}: {
  ticker?: string;
  transcript: any;
}): AssistantChatConfig {
  const previousMessagesUrl =
    `/earnings_call/${transcript.ticker}/messages?year=${transcript.year}&quarter=${transcript.quarter}`;
  const questionUrl =
    `/earnings_call/${transcript.ticker}/questions?year=${transcript.year}&quarter=${transcript.quarter}`;

  return {
    assistant: { name: 'Saturday', avatar: '/images/saturday_avatar.jpg', accent: accentFor('Saturday') },
    breadcrumb: [
      { label: 'Home', href: '/' },
      { label: 'Earnings Calls', href: '/earnings_calls' },
      { label: transcript?.ticker, href: `/ticker/${transcript?.ticker}` },
      { label: `${transcript?.ticker}'s earnings calls`, href: `/earnings_call_list/${transcript?.ticker}` },
      {
        label: `${transcript?.ticker}'s Q${transcript?.quarter} ${transcript?.year} Earnings Call Review with AI Assistant`,
      },
    ],
    sidebarTitle: `${transcript.company} (${transcript?.ticker})'s Q${transcript?.quarter} ${transcript?.year} Earnings Call Analysis`,
    sidebarSubtitle: `Released ${transcript.date}`,
    contextHeader: (
      <>
        <p>
          Price: {transcript.price} | Change:{' '}
          <strong style={{ color: transcript.change_percent_color }}>{transcript.change_percent}</strong>
        </p>
        <p>
          <a
            href={`/earnings_call_data/${transcript.ticker}/${transcript.year}/${transcript.quarter}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {transcript?.ticker}'s Q{transcript?.quarter} {transcript?.year} Earnings Call Transcript
          </a>
        </p>
      </>
    ),
    introContext: (
      <>
        Reviewing <strong>{transcript.company}</strong>'s Q{transcript.quarter} {transcript.year} earnings call (released {transcript.date}).
      </>
    ),
    examples: [
      {
        id: 'earnings',
        label: 'Examples',
        prompts: [
          'Summarize the key points of the earnings call in a list',
          'What are the biggest concerns from the earnings call?',
          'Can you put the financial metrics reported in the call into a table?',
        ],
      },
    ],
    api: {
      previousMessagesUrl,
      send: { mode: 'sync', url: questionUrl },
    },
    waitingActivity: {
      steps: [
        { atMs: 0, label: 'Reviewing the earnings call transcript' },
        { atMs: 3000, label: 'Identifying relevant financial and management details' },
        { atMs: 8000, label: 'Organizing the answer to your question' },
      ],
      longWaitAfterMs: 15000,
      longWaitMessage: 'Still working — detailed analysis can take a little longer.',
    },
    features: {},
    downloadFilenameBuilder: (snippet: string) =>
      `${snippet.slice(0, 20)}_earnings_call_${transcript.ticker}_Q${transcript.quarter}_${transcript.year}_generated_by_ai_assistant.txt`,
    initial: {
      threads: transcript?.conversation_threads || [],
      currentUser: transcript?.current_user,
    },
  };
}

/* ==========================================================================
   Financial Analyst Chat (Lima)
   ========================================================================== */

export function financialAnalystChatConfig({
  agent,
  data,
}: {
  agent: string;
  data: any;
}): AssistantChatConfig {
  const name = agent;
  const isLima = name === 'Lima';
  const previousMessagesUrl = `/financial_analyst/${data.ticker}/previous_messages/${name}`;

  const limaPrompts = [
    `Summarize ${data.ticker}'s latest earnings.`,
    `Analyze ${data.ticker}'s income statements for the last 10 years.`,
    `What are ${data.ticker}'s expectations for the coming quarter and year?`,
  ];

  return {
    assistant: { name, avatar: `/images/${name.toLowerCase()}_avatar.jpg`, accent: accentFor(name) },
    breadcrumb: [
      { label: 'Home', href: '/' },
      { label: 'Find company to analyze', href: '/ai_stock_analysis' },
      { label: data.ticker, href: `/ticker/${data.ticker}` },
      { label: `${data.ticker} Analysis with ${name}` },
    ],
    sidebarTitle: `${data.company_name} (${data.ticker}) Analysis`,
    sidebarSubtitle: data.industry,
    contextHeader: (
      <>
        <p>
          Price: {data.price} | Change:{' '}
          <strong style={{ color: data.change_percent_color }}>{data.change_percent}</strong>
        </p>
      </>
    ),
    introContext: (
      <>
        AI Stock Market Research Analyst here to help you analyze <strong>{data.company_name}</strong>'s stock and financials.
      </>
    ),
    examples: [
      {
        id: 'analyst',
        label: 'Examples',
        prompts: limaPrompts,
      },
    ],
    api: {
      previousMessagesUrl,
      send: {
        mode: 'async',
        postUrl: '/async_chat_messages',
        bodyExtras: { message_type: 'financial_analyst', ticker: data.ticker, agent: name },
        pollIntervals: DEFAULT_POLL_INTERVALS,
        maxPollDuration: DEFAULT_MAX_POLL_DURATION,
        ...(isLima ? { eventsPollInterval: DEFAULT_EVENTS_POLL_INTERVAL } : {}),
      },
    },
    waitingActivity: isLima
      ? {
          steps: [
            { atMs: 0, label: 'Understanding your question' },
            { atMs: 3000, label: 'Planning the research approach' },
            { atMs: 8000, label: 'Preparing the first research step' },
          ],
          longWaitAfterMs: 15000,
          longWaitMessage: 'Still working — detailed research can take a little longer.',
          liveEvents: {
            recentEventLimit: 3,
            activeLabels: {
              thinking: 'Continuing the analysis',
              reasoning: 'Continuing the analysis',
              tool_call: 'Working on this research step',
            },
            defaultActiveLabel: 'Continuing the analysis',
            longWaitMessage: 'Still working — this research step can take a little longer.',
          },
        }
      : undefined,
    features: isLima ? { activityEvents: true } : {},
    downloadFilenameBuilder: (snippet: string) =>
      `${snippet.slice(0, 20)}_analysis_of_${data.ticker}_generated_by_${name}_ai_assistant.txt`,
    initial: {
      threads: data?.conversation_threads || [],
      currentUser: data?.current_user,
    },
  };
}

/* ==========================================================================
   SEC Filing Chat (June)
   ========================================================================== */

export function secFilingChatConfig({
  agent,
  data,
}: {
  agent?: string;
  data: any;
}): AssistantChatConfig {
  const name = agent || 'June';
  const previousMessagesUrl = `/sec_filings/${data.accession_number}/previous_messages?ticker=${data.ticker}`;
  const questionUrl = `/sec_filings/${data.accession_number}/questions`;

  const promptsByForm: Record<string, string[]> = {
    '10-K': [
      `What is ${data.company_name}'s primary sources of revenue, and how have they changed over the past year?`,
      `What is the outlook for ${data.company_name} based on the management discussion in their 10-K?`,
      `Summarize the key financial and product highlights from ${data.company_name}'s the 10-K filing?`,
    ],
    '20-F': [
      `What is ${data.company_name}'s primary sources of revenue, and how have they changed over the past year?`,
      `What is the outlook for ${data.company_name} based on the management discussion in their 10-K?`,
      `Summarize the key financial and product highlights from ${data.company_name}'s the 10-K filing?`,
    ],
    '40-F': [
      `What is ${data.company_name}'s primary sources of revenue, and how have they changed over the past year?`,
      `What is the outlook for ${data.company_name} based on the management discussion in their 10-K?`,
      `Summarize the key financial and product highlights from ${data.company_name}'s the 10-K filing?`,
    ],
    '10-Q': [
      `What were the main factors that contributed to ${data.company_name}'s revenue growth in the reported quarter?`,
      `What are the key risk factors highlighted by ${data.ticker} that could potentially impact future financial performance?`,
      `Summarize the key financial and product highlights from ${data.company_name}'s 10-Q filing?`,
    ],
    '8-K': [
      'What triggered the filing of this 8-K? Was it a routine event or unscheduled?',
      'What are the implications of this filing to investors?',
      'Summarize the key takeaways from the 8-K report',
    ],
    'DEF 14A': [
      `What are the key proposals shareholders will vote on at ${data.company_name}'s upcoming annual meeting?`,
      `Summarize the executive compensation structure and any changes from last year for ${data.company_name}'s top executives.`,
      `What are the board's recommendations on the shareholder proposals, and what is their reasoning?`,
    ],
    'PRE 14A': [
      `What are the key proposals shareholders will vote on at ${data.company_name}'s upcoming annual meeting?`,
      `Summarize the executive compensation structure and any changes from last year for ${data.company_name}'s top executives.`,
      `What are the board's recommendations on the shareholder proposals, and what is their reasoning?`,
    ],
  };

  const prompts = promptsByForm[data.form_type] || [];

  return {
    assistant: { name, avatar: '/images/june_avatar.jpg', accent: accentFor('June') },
    breadcrumb: [
      { label: 'Home', href: '/' },
      { label: 'SEC Filings', href: '/sec_filings' },
      { label: data.ticker, href: `/ticker/${data.ticker}` },
      { label: `${data.ticker}'s SEC Filing`, href: `/sec_filings/${data.ticker}` },
      { label: `${data.ticker}'s Form:${data.form_type} filed:${data.last_modified_date}` },
    ],
    sidebarTitle: `${data.company_name} (${data.ticker}) Form ${data.form_type} Analysis`,
    sidebarSubtitle: data.industry,
    contextHeader: (
      <>
        <p>
          Price: {data.price} | Change:{' '}
          <strong style={{ color: data.change_percent_color }}>{data.change_percent}</strong>
        </p>
        <p>
          <a href={data.filing_url} target="_blank" rel="noopener noreferrer">{data.form_type} Document</a>
          {' | '}
          <a href={data.files_url} target="_blank" rel="noopener noreferrer">Files</a>
        </p>
        <p>Filed on: {data.last_modified_date}</p>
      </>
    ),
    introContext: (
      <>
        Analyzing <strong>{data.company_name}</strong>'s {data.form_type} filing from {data.last_modified_date}.
      </>
    ),
    examples: [{ id: 'filing', label: 'Examples', prompts }],
    api: {
      previousMessagesUrl,
      send: { mode: 'sync', url: questionUrl },
    },
    waitingActivity: {
      steps: [
        { atMs: 0, label: 'Reviewing the SEC filing' },
        { atMs: 3000, label: 'Locating disclosures relevant to your question' },
        { atMs: 8000, label: 'Organizing the answer and supporting details' },
      ],
      longWaitAfterMs: 15000,
      longWaitMessage: 'Still working — detailed analysis can take a little longer.',
    },
    features: { throttledState: true },
    downloadFilenameBuilder: (snippet: string) =>
      `${snippet.slice(0, 20)}_analysis_of_${data.ticker}_generated_by_${name}_ai_assistant.txt`,
    initial: {
      threads: data?.conversation_threads || [],
      currentUser: data?.current_user,
    },
  };
}
